import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { MessageService } from 'primeng/api';

import { AuthService } from '@/core/auth/auth.service';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { CardContent } from '@/shared/components/card-content/card-content';
import { QualidadeFeatureService } from './qualidade-feature.service';
import { QualidadeCompletudeUf } from '@/core/api/sicom-api/registro/medico/qualidade/qualidade-completude.model';
import { QualidadeRadarPrazo } from '@/core/api/sicom-api/registro/medico/qualidade/qualidade-radar-prazo.model';
import { QualidadeSnapshot } from '@/core/api/sicom-api/registro/medico/qualidade/qualidade-snapshot.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

type OpcaoIndicador = { label: string; value: string };

@Component({
    selector: 'app-qualidade-monitoramento',
    standalone: true,
    imports: [CommonModule, FormsModule, CardModule, TableModule, SelectModule, ButtonModule, ChartModule, PainelLateral, SelectEstado, MapaUfCard, CardContent],
    templateUrl: './qualidade-monitoramento.html',
    styleUrl: './qualidade-monitoramento.scss'
})
export class QualidadeMonitoramento implements OnInit {
    readonly indicadores: OpcaoIndicador[] = [
        { label: 'CPF inválido ou nulo', value: 'CPF_INVALIDO_OU_NULO' },
        { label: 'Sexo não informado', value: 'SEXO_NAO_INFORMADO' },
        { label: 'Nascimento inválido', value: 'NASCIMENTO_INVALIDO' },
        { label: 'Especialidade incompleta', value: 'ESPECIALIDADE_INCOMPLETA' },
        { label: 'Formação inconsistente', value: 'FORMACAO_INCONSISTENTE' },
        { label: 'Situação inconsistente', value: 'SITUACAO_INCONSISTENTE' }
    ];

    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    indicadorSelecionado = this.indicadores[0].value;

    /**
     * Seção de histórico/snapshot desabilitada por ora: a tabela
     * USR_SICOM.GESTAO_PENDENCIA_SNAPSHOT ainda não existe no banco (o projeto
     * não tem Flyway/Liquibase, o schema é gerido externamente). Reative (true)
     * depois que o DBA aplicar a DDL — ver SUGESTOES-NOVAS-ESTATISTICAS.md.
     */
    readonly snapshotHabilitado = false;

    completude: QualidadeCompletudeUf[] = [];
    radar: QualidadeRadarPrazo[] = [];
    snapshots: QualidadeSnapshot[] = [];

    dadosGrafico: any = null;
    opcoesGrafico: any = this.montarOpcoesGrafico();

    carregandoCompletude = false;
    carregandoRadar = false;
    carregandoHistorico = false;
    capturando = false;

    private ufTokenRestrita: string | null = null;
    private readonly completudeChange$ = new Subject<void>();
    private readonly radarChange$ = new Subject<void>();
    private readonly historicoChange$ = new Subject<void>();

    constructor(
        private feature: QualidadeFeatureService,
        private authService: AuthService,
        private destroyRef: DestroyRef,
        private messageService: MessageService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.aplicar();
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUfSelecionada(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
    }

    aplicar(): void {
        this.carregarCompletude();
        this.carregarRadar();
        if (this.snapshotHabilitado) {
            this.carregarHistorico();
        }
    }

    onIndicadorChange(): void {
        this.carregarHistorico();
    }

    capturarSnapshot(): void {
        const uf = this.ufSelecionada ?? undefined;
        this.capturando = true;
        this.feature
            .capturarSnapshots(uf)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                // markForCheck: estado gravado em callback assíncrono sob o layout OnPush;
                // sem marcar dirty a view só atualizaria após um clique.
                finalize(() => {
                    this.capturando = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Captura concluída', detail: 'Snapshot registrado com sucesso.' });
                    this.carregarHistorico();
                },
                error: (erro) => {
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.mensagemErro(erro, 'Falha ao capturar o snapshot.') });
                }
            });
    }

    // --- Completude ---

    corScore(score: number): string {
        if (score >= 90) return 'score-alto';
        if (score >= 70) return 'score-medio';
        return 'score-baixo';
    }

    larguraBarra(score: number): string {
        const valor = Math.max(0, Math.min(100, score ?? 0));
        return `${valor}%`;
    }

    get totaisCompletude(): QualidadeCompletudeUf {
        const somas = this.completude.reduce(
            (acc, l) => ({
                totalMedicos: acc.totalMedicos + (l.totalMedicos ?? 0),
                totalComPendencia: acc.totalComPendencia + (l.totalComPendencia ?? 0)
            }),
            { totalMedicos: 0, totalComPendencia: 0 }
        );
        const score = somas.totalMedicos ? 100 * (1 - somas.totalComPendencia / somas.totalMedicos) : 0;
        return { ufSgUf: 'BR', totalMedicos: somas.totalMedicos, totalComPendencia: somas.totalComPendencia, score: Math.round(score * 10) / 10 };
    }

    get completudeOrdenada(): QualidadeCompletudeUf[] {
        return this.completude.slice().sort((a, b) => (a.score ?? 0) - (b.score ?? 0));
    }

    // --- Radar ---

    get totaisRadar(): QualidadeRadarPrazo {
        return this.radar.reduce(
            (acc, l) => ({
                fonte: 'RNE',
                descricaoFonte: 'Total',
                vencido: acc.vencido + (l.vencido ?? 0),
                vence30: acc.vence30 + (l.vence30 ?? 0),
                vence60: acc.vence60 + (l.vence60 ?? 0),
                vence90: acc.vence90 + (l.vence90 ?? 0)
            }),
            { fonte: 'RNE', descricaoFonte: 'Total', vencido: 0, vence30: 0, vence60: 0, vence90: 0 } as QualidadeRadarPrazo
        );
    }

    // --- Loaders ---

    private carregarCompletude(): void {
        this.completudeChange$.next();
        this.carregandoCompletude = true;
        this.carregar(this.feature.consultarCompletude(this.ufSelecionada ?? undefined), this.completudeChange$, {
            next: (dados) => (this.completude = dados),
            error: () => {
                this.completude = [];
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar a completude cadastral.' });
            },
            done: () => (this.carregandoCompletude = false)
        });
    }

    private carregarRadar(): void {
        this.radarChange$.next();
        this.carregandoRadar = true;
        this.carregar(this.feature.consultarRadarPrazos(this.ufSelecionada ?? undefined), this.radarChange$, {
            next: (dados) => (this.radar = dados),
            error: () => {
                this.radar = [];
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar o radar de prazos.' });
            },
            done: () => (this.carregandoRadar = false)
        });
    }

    private carregarHistorico(): void {
        this.historicoChange$.next();
        this.carregandoHistorico = true;
        this.carregar(this.feature.consultarSnapshots(this.indicadorSelecionado, this.ufSelecionada ?? undefined), this.historicoChange$, {
            next: (dados) => {
                this.snapshots = dados.slice().sort((a, b) => String(a.dtSnapshot).localeCompare(String(b.dtSnapshot)));
                this.montarGrafico();
            },
            error: () => {
                this.snapshots = [];
                this.dadosGrafico = null;
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar o histórico de pendências.' });
            },
            done: () => (this.carregandoHistorico = false)
        });
    }

    private carregar<T>(fonte: Observable<T[]>, cancelar$: Subject<void>, cbs: { next: (dados: T[]) => void; error: () => void; done: () => void }): void {
        fonte
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(cancelar$),
                finalize(() => {
                    cbs.done();
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (dados) => cbs.next(dados ?? []),
                error: () => cbs.error()
            });
    }

    // --- Gráfico ---

    private montarGrafico(): void {
        if (!this.snapshots.length) {
            this.dadosGrafico = null;
            return;
        }

        const documentStyle = getComputedStyle(document.documentElement);
        const cor = documentStyle.getPropertyValue('--p-primary-color') || '#2563eb';
        const rotuloIndicador = this.indicadores.find((i) => i.value === this.indicadorSelecionado)?.label ?? this.indicadorSelecionado;

        this.dadosGrafico = {
            labels: this.snapshots.map((s) => this.formatarData(s.dtSnapshot)),
            datasets: [
                {
                    label: rotuloIndicador,
                    data: this.snapshots.map((s) => s.total ?? 0),
                    fill: false,
                    borderColor: cor,
                    backgroundColor: cor,
                    tension: 0.35
                }
            ]
        };
    }

    private montarOpcoesGrafico(): any {
        return {
            maintainAspectRatio: false,
            plugins: {
                legend: { position: 'bottom' }
            },
            scales: {
                y: { beginAtZero: true, ticks: { precision: 0 } }
            }
        };
    }

    private formatarData(valor: string): string {
        if (!valor) return '';
        const partes = String(valor).slice(0, 10).split('-');
        if (partes.length === 3) {
            return `${partes[2]}/${partes[1]}/${partes[0]}`;
        }
        return valor;
    }

    private mensagemErro(erro: unknown, fallback: string): string {
        const mensagem = (erro as { message?: string })?.message;
        return mensagem && mensagem.trim() ? mensagem : fallback;
    }

    // --- UF por token ---

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR' || !/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }
}
